import * as Api from '@ViewModels';
import { useCallback } from 'react';
import type { useAutoSave } from './useAutoSave';

type UseAutoSaveReturn = ReturnType<typeof useAutoSave>;

interface UseEmailStepSaveOptions {
	emailStep: Api.IEmailAutomationStep | null;
	stepId: string | undefined;
	templateId: string | undefined;
	name: string;
	tagToAddOnClick: string;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	autoSave: UseAutoSaveReturn;
}

interface UseEmailStepSaveReturn {
	save: (params?: {
		newSendFrom?: Api.ISendFromOptions | null;
		newTagToAddOnClick?: string;
		newSendToSender?: boolean | null;
		newContactFilter?: Api.IContactsFilterRequest | null;
	}) => Promise<void>;
	onSaveStep: (stepToSave?: Partial<Api.IEmailAutomationStep>) => Promise<void>;
}

/**
 * Custom hook for managing email step save operations.
 * Handles save logic for step content, name, tags, contact filters, and sender options.
 */
export function useEmailStepSave({
	emailStep,
	stepId,
	templateId,
	name,
	tagToAddOnClick,
	skipStepContactFilter,
	autoSave,
}: UseEmailStepSaveOptions): UseEmailStepSaveReturn {
	const save = useCallback(
		(
			params: {
				newSendFrom?: Api.ISendFromOptions | null;
				newTagToAddOnClick?: string;
				newSendToSender?: boolean | null;
				newContactFilter?: Api.IContactsFilterRequest | null;
			} = {}
		) => {
			const {
				newTagToAddOnClick = tagToAddOnClick,
				newContactFilter = skipStepContactFilter,
				newSendFrom = null,
				newSendToSender = null,
			} = params;

			if (!emailStep || !stepId || !templateId) {
				// Return early - missing required data for save operation
				return Promise.resolve();
			}

			const emailStepModel: Api.IEmailAutomationStep = {
				...emailStep,
				name: name || emailStep.name || '',
				tagToAddOnClick: newTagToAddOnClick || '',
				contactFilter: newContactFilter,
			};

			if (newSendFrom !== null) {
				emailStepModel.sendFromOptions = newSendFrom;
			}

			if (newSendToSender !== null) {
				if (!emailStepModel.options) {
					emailStepModel.options = {
						noteVisibility: 'all',
						saveAsNote: true,
					};
				}
				emailStepModel.options = {
					...emailStepModel.options,
					sendToSender: newSendToSender,
				};
			}

			return autoSave.saveStep(stepId, Api.AutomationStepType.Email, emailStepModel);
		},
		[emailStep, name, tagToAddOnClick, skipStepContactFilter, stepId, templateId, autoSave]
	);

	const onSaveStep = useCallback(
		(stepToSave?: Partial<Api.IEmailAutomationStep>) => {
			if (!emailStep || !stepId || !templateId) {
				return Promise.resolve();
			}

			const stepModel: Api.IEmailAutomationStep = {
				...emailStep,
				...(stepToSave || {}),
			};

			return autoSave.saveStep(stepId, Api.AutomationStepType.Email, stepModel);
		},
		[emailStep, stepId, templateId, autoSave]
	);

	return {
		save,
		onSaveStep,
	};
}
